import { useContext } from 'react';
import {
  DateRangePicker as AriaDateRangePicker,
  DateRangePickerProps as AriaDateRangePickerProps,
  Button,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DateSegment,
  DateValue,
  Dialog,
  FieldError,
  Group,
  Heading,
  Label,
  Popover,
  RangeCalendar,
  RangeCalendarStateContext,
  Text,
  ValidationResult,
} from 'react-aria-components';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { styled } from '@mui/material/styles';
import { Maybe } from 'graphql/jsutils/Maybe';

import { formatUsd } from '../../../Utils/GenericFunctions';
import './DateRangePicker.css';

interface DateRangePickerProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  monthRent: Maybe<number[]>;
}

const InlineLabel = styled(Label)`
  display: inline-block;
  position: relative;
  top: 0;
  left: 0;
  transform: translate(-2px, 9px) scale(0.75);
  -webkit-transform: translate(-2px, 9px) scale(0.75);
  -moz-transform: translate(-2px, 9px) scale(0.75);
  -ms-transform: translate(-2px, 9px) scale(0.75);
  padding: 0 8px;
  z-index: 1;
  color: rgba(0, 0, 0, 0.6);
  background-color: var(--field-background);
`;
const PriceLine = styled('div')`
  text-align: center;
`;

function MonthPrice({ monthRent }: { monthRent: number[] }) {
  const state = useContext(RangeCalendarStateContext);
  const price = formatUsd(monthRent[state.visibleRange.start.month - 1]);
  return <PriceLine>{price} / 30 days</PriceLine>;
}

export const DateRangePicker = <T extends DateValue>({
  label,
  description,
  errorMessage,
  monthRent,
  ...props
}: DateRangePickerProps<T>) => {
  return (
    <AriaDateRangePicker {...props}>
      <InlineLabel>{label}</InlineLabel>
      <Group>
        <DateInput slot='start'>{(segment) => <DateSegment segment={segment} />}</DateInput>
        <span aria-hidden='true'>–</span>
        <DateInput slot='end'>{(segment) => <DateSegment segment={segment} />}</DateInput>
        <Button>
          <CalendarTodayIcon />
        </Button>
      </Group>
      {description && <Text slot='description'>{description}</Text>}
      <FieldError>{errorMessage}</FieldError>
      <Popover>
        <Dialog>
          <RangeCalendar>
            <header>
              <Button slot='previous'>◀</Button>
              <Heading />
              <Button slot='next'>▶</Button>
            </header>
            {!!monthRent?.length && <MonthPrice monthRent={monthRent} />}
            <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </AriaDateRangePicker>
  );
};
